import React from "react";
import ReactDOM from 'react-dom';
import { ConnectedRouter } from 'connected-react-router';
import './index.css';
import App from './AppReact';
import * as serviceWorker from './serviceWorker';
import * as AppRiot from "./app";
import { darken } from 'polished';
import { cssVarStr } from "./utils/react";
import { initCommunication, InitResult } from "./communication/Communication";
import { Provider } from "react-redux";
import { Switch, Route, Router } from "react-router";
import { createBrowserHistory } from "history";
import { AppState, Fetchable_Success } from "./store/appState";
import { initializeIcons } from "@fluentui/react/lib/Icons";
import { ThemeProvider, createGlobalStyle } from 'styled-components';
import { EntryValue, convertIncorrectEntry, UrlParam } from "./constants/urlParams";
import { AppPaths } from "./constants/urls";
import { ai, getAppInsights, setSharedProperties } from "./telemetry/telemetryService";
import { TelemetryHelper } from "./telemetry/TelemetryHelper";
import TelemetryProvider from "./telemetry/TelemetryProvider";
import TurndownService from "turndown";
import ScrollToTop from "./components/shared/ScrollToTop";
import { appInsightsId, getSlugFromSpaceId, loadServerConfig, server } from "./config/serverConfig";
import { SpaceId } from "./config/spaces/spaceIds";
import AdminView from "./components/Admin/AdminView";

//TODO: check for IE11 and display "update browser" prompt
// SSR: https://github.com/Apak00/react-dotnet-ssr

console.log(`Launch URL: ${window.location.href}`);

initializeIcons();

// Need this to convert html text generated by to AnswerHub to markdown
// Can be removed once we do mass conversion of those items in new backend
export const turndownService = new TurndownService();
// Default turndown rules are not handling <pre> tags correctly
turndownService.addRule('strikethrough', {
    filter: ['pre'],
    replacement: (content, node, options) => '```' + options.br + '\n' + content + options.br + '\n```'
});

// Pass true to initCommunication to use fake native process
const params = new URLSearchParams(window.location.search);

// If initial url is for ticket details view but still has params to connect to native process
// then it couldn't have been launched from VS so remove those params in order to avoid showing the 'can't connect' dialog
if (window.location.pathname.startsWith(AppPaths.t) && params.has(UrlParam.Port)) {
    params.delete(UrlParam.Port);
    params.delete(UrlParam.Fsid);
    window.location.href = `${window.location.origin}${window.location.pathname}?${params.toString()}`;
}

const useFakeNativeProcess = params.get("fakeProcess");
const initResult: InitResult = initCommunication(useFakeNativeProcess === "true");

const baseUrl = document.getElementsByTagName("base")[0].getAttribute("href")!;
const history = createBrowserHistory({ basename: baseUrl });
const rootElement = document.getElementById("root");

(window as any).routerHistory = history;

// Telemetry
ai.initialize(appInsightsId, history);
const appInsights = getAppInsights();

const telemetryHelper = new TelemetryHelper(appInsights, (data: Map<string, string>) => {
    setSharedProperties(data);
});

// load server-injected configuration and log errors if any
loadServerConfig(telemetryHelper);

// check for ASP.NET core server errors - sent by backend in URI
if (params.has(UrlParam.ErrorId)) {
    const errorMessage = `Error request ID: ${params.get(UrlParam.ErrorId)}`;
    console.error(`ERR_ASP_NET_CORE: ${errorMessage}`);
    telemetryHelper.postFault("ERR_ASP_NET_CORE", errorMessage);

    params.delete(UrlParam.ErrorId);
    history.push(`${window.location.pathname}?${params.toString()}`);
}

const partialInitState: Partial<AppState> = {
    mustAuthenticate: initResult.mustAuthenticate,
    openServerConfig: server ? Fetchable_Success(server) : null,
};
new AppRiot.App(initResult, partialInitState as any, telemetryHelper, history);
AppRiot.App.initializeHomePage();

const GlobalStyle = createGlobalStyle`
.btn-semi-compact-more-options {
    display: inline-flex;
    align-items: center;
    border: none;
    border-radius: .2rem;
    font-size: 1.8rem;
    font-weight: 600;
    min-height: 4rem;
    padding: 0 1.2rem;
        
    min-height: unset;
    height: auto;
    padding: 0.2rem .5rem;

    &:hover {
        background-color: ${darken(0.15, cssVarStr("--color-background"))};
    }

    &:disabled, &:disabled:hover {
        background-color: var(--color-background);
        cursor: default;
    }
    
    &:focus {
        outline: solid .1rem var(--titlebar-text-color);
    }
}
`

// Redirect to correct route based on entry param
// nikolami: 2022-10-13: using ton of legacy rewrite logic - now removing "?entry" and "&space" completely and adding /slug for space
if (history.location.pathname === "/" || history.location.pathname === AppPaths.report) {
    // cases 2021-04-16:
    // VS 2017:
    // a. https://developercommunity.visualstudio.com/report  <-- for report problem, from https://go.microsoft.com/fwlink/?linkid=2130013
    // b. https://developercommunity.visualstudio.com  <-- for provide a suggestion, from https://go.microsoft.com/fwlink/?LinkId=624145
    //
    // VS 2019:
    // 1. https://developercommunity.visualstudio.com/report?entry=myfeedback
    // 2. https://developercommunity.visualstudio.com/report?entry=problem
    // 3. https://developercommunity.visualstudio.com/report?entry=suggestion
    //
    // VS Installer:
    // 4. https://developercommunity.visualstudio.com/report
    // 5. https://developercommunity.visualstudio.com/report?entry=problem
    // 6. https://developercommunity.visualstudio.com/report?entry=suggestion
    // 7. https://developercommunity.visualstudio.com/?entry=report   <-- note: different spelling
    // 8. https://developercommunity.visualstudio.com/?entry=suggest  <-- note: different spelling
    //
    // VS for Mac
    // https://aka.ms/feedback/suggest?space=41 (2022-10-19: fwlink resolve: https://developercommunity.visualstudio.com/report?entry=suggestion&space=41)
    // https://aka.ms/feedback/report?space=41 (2022-10-19: fwlink resolve: https://developercommunity.visualstudio.com/report?entry=problem&space=41)
    //
    // Other:
    // 9. https://developercommunity.visualstudio.com/?entry=myactivity <-- hidden from VS but accessible through Profile and Preferences

    let entry: EntryValue = convertIncorrectEntry(params.get(UrlParam.Entry)); // convert spelling for cases 7 and 8
    const isReportPathWithUnclearEntryPoint: boolean = (history.location.pathname === AppPaths.report && (!entry)); // case 4. above: "/report" URL, but value of "entry" is unknown
    if (isReportPathWithUnclearEntryPoint) { // "/report" url and unclear entry point, assume "report problem" scenario
        entry = EntryValue.problem;
    }

    const spaceIdStr: string | null = params.get(UrlParam.SpaceId);
    let spaceId: SpaceId | null = spaceIdStr ? parseInt(spaceIdStr) : null;
    if (isNaN(spaceId) || spaceId < 0) {
        spaceId = null;
    }
    const slugOrVs: string = getSlugFromSpaceId(spaceId ?? SpaceId.VS);

    params.delete(UrlParam.Entry);
    params.delete(UrlParam.SpaceId);

    const newParams = params.toString();
    const newSearch = newParams === "" ? "" : "?" + newParams;

    if (!entry) { // ...not a problem/suggestion, and no entry -> go to home
        history.replace(`${AppPaths.home}${newSearch}`);
    }
    else if (entry === EntryValue.problem) { // if report problem
        history.replace(`/${slugOrVs}${AppPaths.report}${newSearch}`);
    } else if (entry === EntryValue.suggestion) { // if suggest a feature
        history.replace(`/${slugOrVs}${AppPaths.suggest}${newSearch}`);
    } else if (entry === EntryValue.myfeedback) { // "myfeedback" -> go to home
        history.replace(`${AppPaths.home}${newSearch}`);
    } else if (entry === EntryValue.myactivity) { // "myactivity" -> go to my activity page
        history.replace(`${AppPaths.myactivity}${newSearch}`);
    }
}

// rewrite old URLs /search?spaceId=12345 -> /SlugForSpace12345
if (history.location.pathname === AppPaths.search) {
    const spaceIdStr: string | null = params.get(UrlParam.SpaceId);
    let spaceId: SpaceId | null = spaceIdStr ? parseInt(spaceIdStr) : null;
    if (isNaN(spaceId) || spaceId < 0) {
        spaceId = null;
    }
    const slugOrVs: string = getSlugFromSpaceId(spaceId ?? SpaceId.VS);

    params.delete(UrlParam.SpaceId);
    const newParams = params.toString();
    const newSearch = newParams === "" ? "" : "?" + newParams;
    history.replace(`/${slugOrVs}${newSearch}`);
}

// Uncomment for debugging profile and preferences
// history.push(`/user/my/${AppPaths.profile}?fakeProcess=true`);

// @TODO: Pass theme
ReactDOM.render(
    <Provider store={AppRiot.App.store}>
        <ConnectedRouter history={history}>
            <ThemeProvider theme={{}}>
                <GlobalStyle />
                <Router history={history}>
                    <ScrollToTop />
                    <TelemetryProvider>
                        <Switch>
                            <Route path="/admin/:path?" component={AdminView} />
                            <Route path="*" component={App} />
                        </Switch>
                    </TelemetryProvider>
                </Router>
            </ThemeProvider>
        </ConnectedRouter>
    </Provider>,
    rootElement);

// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: https://bit.ly/CRA-PWA
serviceWorker.unregister();
